import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellDot } from "lucide-react";
import { useCakaNaTeba } from "@/api/hooks/useZakazky";
import { useKoncepty, type Koncept } from "@/api/hooks/useTopsolidKoncept";
import { useImport } from "@/lib/import/ImportContext";

/** Najviac vecí v páse, kým ho user nerozbalí. */
const NAJVIAC = 3;

interface Vec {
  kluc: string;
  cislo: string;
  text: string;
  otvor: () => void;
}

function textKonceptu(k: Koncept): { cislo: string; text: string } {
  const cislo = k.cislo_zakazky || k.nazov || k.nazov_suboru;
  if (k.zdroj === "regal") return { cislo, text: "diely regálu na nacenenie" };
  if (k.zdroj === "zostavy") return { cislo, text: "nový import z Tandem Zostáv" };
  return { cislo, text: "rozrobený import" };
}

/**
 * Pás **Čaká na teba** nad prehľadom zákaziek (KOLO 242, návrh P4) — čo
 * prihlásený má urobiť: rozrobené importy (aj diely regálu na nacenenie),
 * ponuky bez zmeny 14 dní, tovar bez ceny, dokončené bez kalkulácie.
 * Server vráti len to, na čo má prihlásený právo; prázdny pás sa neukáže.
 */
export function CakaNaTebaPas({ importy }: { importy: boolean }): JSX.Element | null {
  const navigate = useNavigate();
  const { otvorKoncept } = useImport();
  const [vsetko, setVsetko] = useState(false);
  const zoServera = useCakaNaTeba().data?.data ?? [];
  const koncepty = useKoncepty().data?.data ?? [];

  const veci: Vec[] = [
    ...(importy ? koncepty : []).map((k) => ({
      kluc: `k-${k.id}`,
      ...textKonceptu(k),
      otvor: () => otvorKoncept(k.id),
    })),
    ...zoServera.map((p) => ({
      kluc: `${p.druh}-${p.zakazka_id}`,
      cislo: p.cislo,
      text: p.text,
      otvor: () => navigate(`/zakazky/${p.zakazka_id}`),
    })),
  ];
  if (veci.length === 0) return null;
  const ukaz = vsetko ? veci : veci.slice(0, NAJVIAC);

  return (
    <section
      aria-label="Čaká na teba"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
    >
      <span className="flex shrink-0 items-center gap-1.5 font-ui text-xs font-extrabold uppercase tracking-wider">
        <BellDot className="h-4 w-4" aria-hidden />
        Čaká na teba
      </span>
      {ukaz.map((v, i) => (
        <span key={v.kluc} className="flex min-w-0 items-center gap-2">
          {i > 0 ? (
            <span aria-hidden className="hidden text-amber-700 sm:inline">
              ·
            </span>
          ) : null}
          <button
            type="button"
            onClick={v.otvor}
            className="min-w-0 truncate text-left hover:underline"
            title="Otvoriť"
          >
            <b className="font-mono font-semibold">{v.cislo}</b> — {v.text}
          </button>
        </span>
      ))}
      {veci.length > NAJVIAC ? (
        <button
          type="button"
          onClick={() => setVsetko(!vsetko)}
          className="ml-auto shrink-0 text-xs font-semibold text-amber-800 hover:underline"
        >
          {vsetko ? "Menej" : `+ ${veci.length - NAJVIAC} ďalšie`}
        </button>
      ) : null}
    </section>
  );
}
